import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'

const colors = {
  // Redesign primary badges
  primary: 'bg-primary/10 text-primary border-2 border-primary/30 group-data-[hover]:bg-primary/20',
  secondary: 'bg-secondary text-secondary-foreground border-2 border-border/50 group-data-[hover]:bg-secondary/80',
  accent: 'bg-accent/10 text-accent-foreground border-2 border-accent/30 group-data-[hover]:bg-accent/20',
  muted: 'bg-muted text-muted-foreground border border-border/50 group-data-[hover]:bg-muted/80',
  destructive: 'bg-destructive/10 text-destructive border-2 border-destructive/30 group-data-[hover]:bg-destructive/20',
  // Legacy color badges
  red: 'bg-red-100 text-red-700 border border-red-200 group-data-[hover]:bg-red-200',
  orange: 'bg-orange-100 text-orange-700 border border-orange-200 group-data-[hover]:bg-orange-200',
  amber: 'bg-amber-100 text-amber-700 border border-amber-200 group-data-[hover]:bg-amber-200',
  yellow: 'bg-yellow-100 text-yellow-700 border border-yellow-200 group-data-[hover]:bg-yellow-200',
  lime: 'bg-lime-100 text-lime-700 border border-lime-200 group-data-[hover]:bg-lime-200',
  green: 'bg-green-100 text-green-700 border border-green-200 group-data-[hover]:bg-green-200',
  emerald: 'bg-emerald-100 text-emerald-700 border border-emerald-200 group-data-[hover]:bg-emerald-200',
  teal: 'bg-teal-100 text-teal-700 border border-teal-200 group-data-[hover]:bg-teal-200',
  cyan: 'bg-cyan-100 text-cyan-700 border border-cyan-200 group-data-[hover]:bg-cyan-200',
  sky: 'bg-sky-100 text-sky-700 border border-sky-200 group-data-[hover]:bg-sky-200',
  blue: 'bg-blue-100 text-blue-700 border border-blue-200 group-data-[hover]:bg-blue-200',
  indigo: 'bg-indigo-100 text-indigo-700 border border-indigo-200 group-data-[hover]:bg-indigo-200',
  violet: 'bg-violet-100 text-violet-700 border border-violet-200 group-data-[hover]:bg-violet-200',
  purple: 'bg-purple-100 text-purple-700 border border-purple-200 group-data-[hover]:bg-purple-200',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 group-data-[hover]:bg-fuchsia-200',
  pink: 'bg-pink-100 text-pink-700 border border-pink-200 group-data-[hover]:bg-pink-200',
  rose: 'bg-rose-100 text-rose-700 border border-rose-200 group-data-[hover]:bg-rose-200',
  zinc: 'bg-zinc-100 text-zinc-700 border border-zinc-200 group-data-[hover]:bg-zinc-200',
}

type BadgeProps = { color?: keyof typeof colors }

export function Badge({ color = 'muted', className, ...props }: BadgeProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-full px-3 py-1 text-sm font-medium forced-colors:outline',
        colors[color]
      )}
    />
  )
}

export const BadgeButton = forwardRef(function BadgeButton(
  {
    color = 'zinc',
    className,
    children,
    ...props
  }: BadgeProps & { className?: string; children: React.ReactNode } & (
      | Omit<Headless.ButtonProps, 'className'>
      | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
    ),
  ref: React.ForwardedRef<HTMLElement>
) {
  let classes = clsx(
    className,
    'group relative inline-flex rounded-md focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500'
  )

  return 'href' in props ? (
    <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={classes} ref={ref}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Headless.Button>
  )
})
